import { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const VictorianADUDesignSantaClara = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => {
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    setShowCalendly(false);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Victorian ADU Design & Construction in Santa Clara",
    "description": "Custom Victorian ADUs in Santa Clara. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/victorian-adu-design-santa-clara"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/victorian-adu-design-santa-clara" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden">
            <button 
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-900 to-purple-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Victorian ADU Design in Santa Clara
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-purple-100">
              Create elegant Victorian ADUs that honor classical architecture while meeting modern living needs in Santa Clara
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openCalendlyModal}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                Get Free Design Consultation
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-purple-900 px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Victorian Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Victorian ADU Design Elements
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Ornate Gingerbread Trim</h3>
                <p className="text-gray-600">
                  Intricate decorative woodwork and trim details that capture the essence of Victorian craftsmanship while meeting Santa Clara building standards.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Bay Windows</h3>
                <p className="text-gray-600">
                  Classic bay and bow windows that maximize natural light and create charming interior nooks perfect for reading or relaxation.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Steep Rooflines</h3>
                <p className="text-gray-600">
                  Dramatic rooflines with dormers and turrets that create architectural interest while maximizing interior space and storage.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Rich Color Palettes</h3>
                <p className="text-gray-600">
                  Sophisticated paint schemes with multiple accent colors that highlight architectural details and create visual depth.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Wraparound Porches</h3>
                <p className="text-gray-600">
                  Inviting covered porches with decorative columns and railings that extend living space and create welcoming entrances.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Period Hardware</h3>
                <p className="text-gray-600">
                  Authentic Victorian-style door handles, hinges, and fixtures that maintain historical accuracy with modern functionality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Santa Clara Specific Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Why Victorian ADUs Work in Santa Clara
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-purple-600">Historical Character</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Complements Santa Clara's historic neighborhoods and heritage
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Increases property value through architectural distinction
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Creates unique rental appeal in the competitive market
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Preserves neighborhood character while adding density
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-purple-600">Modern Functionality</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Energy-efficient systems hidden within period design
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Smart home technology integrated seamlessly
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Rental income potential up to $4,200/month
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2">•</span>
                    Meets all Santa Clara County building codes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Our Victorian ADU Design Process
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-semibold mb-3">Historical Research</h3>
                <p className="text-gray-600">Study Santa Clara's Victorian heritage to inform authentic design elements.</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-semibold mb-3">Custom Design</h3>
                <p className="text-gray-600">Create Victorian plans that balance period authenticity with modern comfort.</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-semibold mb-3">Permit Approval</h3>
                <p className="text-gray-600">Navigate Santa Clara's permitting with expertise in historical design requirements.</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-semibold mb-3">Crafted Construction</h3>
                <p className="text-gray-600">Build with attention to Victorian details using modern materials and techniques.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-purple-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Victorian ADU in Santa Clara?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Let's create an elegant Victorian ADU that honors architectural tradition while providing modern comfort and rental income potential.
          </p>
          <Button 
            onClick={openCalendlyModal}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-colors"
          >
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>

      <FloatingADUWizard />
      <Footer />
    </div>
  );
};

export default VictorianADUDesignSantaClara;